import { useMemo, useState } from 'react';
import { CARD_SIZES, DEFAULT_MARKS, PAPER_SIZES, PRESET_SHORT_EDGE } from '../lib/constants';
import { effectiveCard, validateCardFitsPaper } from '../lib/geometry';
import { recropSlot, revokeSlotImage } from '../lib/canvas';
import type {
  CardSize,
  Drift,
  MarkSettings,
  Orientation,
  PaperSize,
  PrinterSettings,
  SlotId,
  SlotImage,
  SlotMap,
} from '../types';

export interface AppState {
  cardSize: CardSize;
  paperSize: PaperSize;
  orientation: Orientation;
  /** When true, uploaded images keep their native source resolution in the
   *  embedded JPEGs (skip resample). Bigger PDF, more detail for fine prints. */
  preserveResolution: boolean;
  /** Single-sided (false, front + back only) vs two-sided (true, with inside). */
  insideEnabled: boolean;
  slots: SlotMap;
  drift: Drift;
  marks: MarkSettings;
  printer: PrinterSettings;

  setCardSize: (next: CardSize) => void;
  setPaperSize: (next: PaperSize) => void;
  setOrientation: (next: Orientation) => void;
  setPreserveResolution: (next: boolean) => void;
  /** Toggle the inside-content card type. Going false also clears any
   *  inside-* slots so the PDF/preview drop them. */
  setInsideEnabled: (next: boolean) => void;
  setSlot: (slotId: SlotId, next: SlotImage | undefined) => void;
  setDrift: (next: Drift) => void;
  setMarks: (next: MarkSettings) => void;
  setPrinter: (next: PrinterSettings) => void;

  effectiveCardWidthIn: number;
  effectiveCardHeightIn: number;
  effectiveCardWidthPx: number;
  effectiveCardHeightPx: number;

  fitError: string | null;
  hasFront: boolean;
  hasInside: boolean;
  downloadDisabled: boolean;
  downloadDisabledReason: string | undefined;
}

/** Re-crop every slot in the map against new panel dimensions, preserving
 *  each slot's stored cropX/cropY. Done in parallel; returns the new map. */
const recropAllSlots = async (
  slots: SlotMap,
  panelWidthPx: number,
  panelHeightPx: number,
  preserveResolution: boolean
): Promise<SlotMap> => {
  const entries = Object.entries(slots) as Array<[SlotId, SlotImage]>;
  const recropped = await Promise.all(
    entries.map(async ([id, slot]) => {
      try {
        const next = await recropSlot(slot, panelWidthPx, panelHeightPx, {
          preserveResolution,
        });
        return [id, next] as const;
      } catch {
        return [id, null] as const;
      }
    })
  );
  const out: SlotMap = {};
  for (const [id, next] of recropped) {
    if (next) out[id] = next;
  }
  return out;
};

export function useAppState(): AppState {
  const [cardSize, setCardSize] = useState<CardSize>(CARD_SIZES['5x7']);
  const [paperSize, setPaperSize] = useState<PaperSize>(PAPER_SIZES.letter);
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [preserveResolution, setPreserveResolution] = useState(false);
  // Default single-sided. Sample-loaders / Studio toggle this to true to
  // unlock inside slots, the inside preview, and the duplex / calibration
  // sections of the Print settings.
  const [insideEnabled, setInsideEnabled] = useState<boolean>(false);
  const [slots, setSlots] = useState<SlotMap>({});
  const [drift, setDrift] = useState<Drift>({ dxMm: 0, dyMm: 0 });
  const [marks, setMarks] = useState<MarkSettings>(DEFAULT_MARKS);
  const [printer, setPrinter] = useState<PrinterSettings>(PRESET_SHORT_EDGE);

  const setSlot = (slotId: SlotId, next: SlotImage | undefined) => {
    setSlots((prev) => {
      revokeSlotImage(prev[slotId]);
      const out = { ...prev };
      if (next) out[slotId] = next;
      else delete out[slotId];
      return out;
    });
  };

  // Card-size and orientation changes both invalidate the cached crop on each
  // existing slot (panel aspect changes). Instead of dropping the image, we
  // re-crop from the retained `source` blob at the new panel dimensions,
  // preserving the stored cropX / cropY so the user's framing intent is kept.
  const handleSetCardSize = async (next: CardSize) => {
    if (next === cardSize) return;
    const newEffC = effectiveCard(next, orientation);
    const newSlots = await recropAllSlots(
      slots,
      newEffC.widthPx,
      newEffC.heightPx,
      preserveResolution
    );
    setSlots((prev) => {
      for (const k of Object.keys(prev) as SlotId[]) revokeSlotImage(prev[k]);
      return newSlots;
    });
    setCardSize(next);
  };

  const handleSetOrientation = async (next: Orientation) => {
    if (next === orientation) return;
    const newEffC = effectiveCard(cardSize, next);
    const newSlots = await recropAllSlots(
      slots,
      newEffC.widthPx,
      newEffC.heightPx,
      preserveResolution
    );
    setSlots((prev) => {
      for (const k of Object.keys(prev) as SlotId[]) revokeSlotImage(prev[k]);
      return newSlots;
    });
    setOrientation(next);
  };

  const handleSetPreserveResolution = async (next: boolean) => {
    if (next === preserveResolution) return;
    const effC = effectiveCard(cardSize, orientation);
    const newSlots = await recropAllSlots(slots, effC.widthPx, effC.heightPx, next);
    setSlots((prev) => {
      for (const k of Object.keys(prev) as SlotId[]) revokeSlotImage(prev[k]);
      return newSlots;
    });
    setPreserveResolution(next);
  };

  const handleSetInsideEnabled = (next: boolean) => {
    if (next === insideEnabled) return;
    setInsideEnabled(next);
    if (!next) {
      // Clear inside slots so the PDF / preview no longer include them.
      setSlots((prev) => {
        if (!prev.insideLeft && !prev.insideRight) return prev;
        revokeSlotImage(prev.insideLeft);
        revokeSlotImage(prev.insideRight);
        const { insideLeft: _l, insideRight: _r, ...rest } = prev;
        return rest;
      });
    }
  };

  const effC = useMemo(() => effectiveCard(cardSize, orientation), [cardSize, orientation]);

  const fitError = useMemo(
    () => validateCardFitsPaper(paperSize, cardSize, orientation),
    [paperSize, cardSize, orientation]
  );
  const hasFront = Boolean(slots.front);
  const hasInside = Boolean(slots.insideLeft || slots.insideRight);
  const downloadDisabled = !hasFront || fitError !== null;
  const downloadDisabledReason =
    fitError ?? (!hasFront ? 'Add a front image to generate the PDF.' : undefined);

  return {
    cardSize,
    paperSize,
    orientation,
    preserveResolution,
    insideEnabled,
    slots,
    drift,
    marks,
    printer,
    setCardSize: handleSetCardSize,
    setPaperSize,
    setOrientation: handleSetOrientation,
    setPreserveResolution: handleSetPreserveResolution,
    setInsideEnabled: handleSetInsideEnabled,
    setSlot,
    setDrift,
    setMarks,
    setPrinter,
    effectiveCardWidthIn: effC.widthIn,
    effectiveCardHeightIn: effC.heightIn,
    effectiveCardWidthPx: effC.widthPx,
    effectiveCardHeightPx: effC.heightPx,
    fitError,
    hasFront,
    hasInside,
    downloadDisabled,
    downloadDisabledReason,
  };
}
